import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import {
  ShieldCheck,
  LogOut,
  ListChecks,
  Users,
  Receipt,
  LayoutDashboard,
  ArrowUpFromLine,
  LifeBuoy,
  FileText,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, tid: "admin-nav-overview" },
  { to: "/admin/kyc", label: "KYC queue", icon: ListChecks, tid: "admin-nav-kyc" },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowUpFromLine, tid: "admin-nav-withdrawals" },
  { to: "/admin/tickets", label: "Support", icon: LifeBuoy, tid: "admin-nav-tickets" },
  { to: "/admin/users", label: "Users", icon: Users, tid: "admin-nav-users" },
  { to: "/admin/transactions", label: "Transactions", icon: Receipt, tid: "admin-nav-transactions" },
  { to: "/admin/statements", label: "Statements", icon: FileText, tid: "admin-nav-statements" },
];

export default function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F0ECE0]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#F0ECE0]/85 border-b border-[#D2CDBE]">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
            <Logo variant="mark" size={36} />
            <div>
              <div className="font-display font-bold tracking-tight leading-none">FinVault</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#4F4D45] mt-0.5">Compliance console</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon, end, tid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={tid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive ? "bg-[#1B3526] text-white" : "text-[#0A0A08] hover:bg-[#E3DECF]"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.6} />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 text-sm text-[#4F4D45]">
            <ShieldCheck className="w-4 h-4 text-[#1B3526]" />
            <span data-testid="admin-user-email" className="hidden lg:inline">{user?.email}</span>
            <Button onClick={async () => { await logout(); navigate("/login"); }} variant="ghost" className="text-[#4F4D45] hover:text-[#992F2F]" data-testid="admin-logout">
              <LogOut className="w-4 h-4 lg:mr-1" />
              <span className="hidden lg:inline">Sign out</span>
            </Button>
          </div>
        </div>
        <div className="md:hidden border-t border-[#D2CDBE] flex overflow-x-auto gap-2 px-4 py-2 bg-white/50">
          {NAV.map(({ to, label, icon: Icon, end, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`${tid}-mobile`}
              className={({ isActive }) =>
                `shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
                  isActive ? "bg-[#1B3526] text-white" : "bg-white border border-[#D2CDBE]"
                }`
              }
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </NavLink>
          ))}
        </div>
      </header>
      <main className="max-w-[1280px] mx-auto px-6 md:px-10 py-8">{children}</main>
    </div>
  );
}
